import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField';
import { FormFieldWithInfo } from '@openx/components-core/lib/FormFieldWithInfo/FormFieldWithInfo';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField';
import { RHFTextFieldWithAdornment } from '@openx/components-core/lib/TextField/RHFTextFieldWithAdornment';
import { ACL_TYPE } from '@openx/types/aclType';
import { type DealFormData, dealFormField, type PmpDealType, pmpDealType } from '@openx/types/deal';
import type { OptionsMap } from '@openx/types/options';
import { UserRole } from '@openx/types/user';
import { currencySymbol } from '@openx/utils/lib/priceFormat';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import {
  AMAZON_DEMAND_PARTNER_ID,
  DEAL_SOURCE_DSP_SOURCED,
  DV360_DEMAND_PARTNER_ID,
  TTD_DEMAND_PARTNER_ID,
} from '@openx/utils/services/config';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDealFormContext } from '../../../DealForm/DealForm.context';
import { PDP_SYNC_PERIOD_DEFAULT } from '../../../DealForm/utils/constants';
import { useDealContext } from '../../../utils/context/DealContext';
import type { IParticipantFunctions } from '../../../utils/participantFunctions';
import { DealParticipantsField } from '../fields';
import { useAmazonSyncMarketplaceConfirmation } from '../hooks/useAmazonSyncMarketplaceConfirmation';
import { useCanCreateAmazonMarketplaceDeal } from '../hooks/useCanCreateAmazonMarketplaceDeal';
import { useCheckBuyerSeatRestriction } from '../hooks/useCheckBuyerSeatRestriction';
import { isDemandPartnerDropdownDisabled, isToggleEnableDV360IPVisible } from '../utils/Participant-utils';
import { sortDSPOptions } from '../utils/sortDSPOptions';
import { ParticipantBrands } from './ParticipantBrands';
import { ParticipantBuyers } from './ParticipantBuyers';
import { ParticipantBuyersFreeForm } from './ParticipantBuyersFreeForm';

type ParticipantsProps = {
  blockedToSelectPartnerIds?: string[];
  dealType: PmpDealType | null;
  demandPartnerOptions: OptionsMap;
  demandPartnerOptionsLoading: boolean;
  index: number;
  isEdit: boolean;
  participantFunctions: IParticipantFunctions;
  remove: (index: number) => void;
  selectedDemandPartners: string[];
  showDivider: boolean;
  showRemove: boolean;
};

export const Participant = ({
  blockedToSelectPartnerIds,
  dealType,
  demandPartnerOptions,
  demandPartnerOptionsLoading,
  index,
  isEdit,
  participantFunctions,
  remove,
  selectedDemandPartners,
  showDivider,
  showRemove,
}: ParticipantsProps) => {
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed, accessUser } = useIsAllowed();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { setValue, control, trigger, getValues } = useFormContext();
  const fieldPrefix = `${DealParticipantsField.DEAL_PARTICIPANTS}[${index}].`;
  const { dealInitialValues } = useDealFormContext();

  const observableDealType = useWatch({ control, name: dealFormField.PMP_DEAL_TYPE });
  const observableIsDV360IP = useWatch({ control, name: dealFormField.ENABLE_DV360_INVENTORY_PACKAGE });
  const observableDealSource = useWatch({ control, name: dealFormField.DEAL_SOURCE });
  const observableDemandPartner = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.DSP}` });
  const observableEndDate = useWatch({ control, name: dealFormField.END_DATE });
  const observableCurrency = useWatch<DealFormData, typeof dealFormField.CURRENCY>({
    name: dealFormField.CURRENCY,
  });

  const isDV360DemandPartner = observableDemandPartner === DV360_DEMAND_PARTNER_ID;
  const isAmazonDemandPartner = observableDemandPartner === AMAZON_DEMAND_PARTNER_ID;
  const isTTDPartner = observableDemandPartner === TTD_DEMAND_PARTNER_ID;

  const isTTDPartnerAndGuaranteedDealType =
    observableDemandPartner === TTD_DEMAND_PARTNER_ID && observableDealType === pmpDealType.Guaranteed;

  const isTTDAndNotGuaranteed = isTTDPartner && observableDealType !== pmpDealType.Guaranteed;
  const initialMediaSpendGoal = dealInitialValues?.pdp_config?.media_spend_goal ?? null;

  const { isOa, useBuyersFetch, useOnBehalfOfAccountId } = useDealContext();

  const { showAmazonMarketplaceConfirmation } = useAmazonSyncMarketplaceConfirmation();

  const { isPub, isDsp } = usePermissionContext();
  const showParticipantMediaSpendGoal = isTTDAndNotGuaranteed && (!isEdit || initialMediaSpendGoal !== null);
  const showTTDCommitmentSectionAlert = isPub && isTTDPartner && observableDealType === pmpDealType.Guaranteed;

  const isDV360OnEditEntry = useRef<boolean>(isEdit && isDV360DemandPartner && observableIsDV360IP);

  const onBehalfAccountId = useOnBehalfOfAccountId();

  const isOaInternal = !!accessUser?.roles.includes(UserRole.NETWORK_OA_INTERNAL);
  const isExternalUser = !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) && !isOaInternal;

  const canCreateAmazonMarketplaceDeal = useCanCreateAmazonMarketplaceDeal();

  const showParticipantBuyersFreeForm = isAmazonDemandPartner && isExternalUser;
  const showParticipantBudget = isPub && isAmazonDemandPartner && observableDealType === pmpDealType.Guaranteed;

  const isSyncEnabledOnLoad =
    isEdit &&
    dealInitialValues?.synchronized_with?.amazon_sync !== null &&
    dealInitialValues?.synchronized_with?.amazon_sync !== undefined;

  const observableAmazonMarketplace = useWatch({
    control,
    name: `${fieldPrefix}${DealParticipantsField.AMAZON_MARKETPLACE_ENABLED}`,
  });

  const isMarketplaceEnabledOnLoad =
    isEdit && !!dealInitialValues?.deal_participants?.[index]?.amazon_sync_marketplace_enabled;

  // Create is ACL-gated, and existing marketplace deals stay visible (disabled) on edit.
  const showAmazonMarketplaceCheckbox =
    isAmazonDemandPartner && (isDsp || isOa) && (canCreateAmazonMarketplaceDeal || isMarketplaceEnabledOnLoad);

  const isAmazonMarketplaceActive = !!observableAmazonMarketplace;

  const showParticipantBuyers = observableDemandPartner && !showParticipantBuyersFreeForm && !isAmazonMarketplaceActive;

  const demandPartnerIdForFetch = showParticipantBuyersFreeForm ? null : observableDemandPartner;
  const { mapById: buyers, loading: isBuyersLoading } = useBuyersFetch(demandPartnerIdForFetch, onBehalfAccountId);

  useCheckBuyerSeatRestriction({
    fieldPrefix,
    demandPartnerOptions,
    demandPartnerOptionsLoading,
  });

  const handlePartnerChange = useCallback(
    (_e, value) => {
      if (
        !blockedToSelectPartnerIds ||
        blockedToSelectPartnerIds.length === 0 ||
        !blockedToSelectPartnerIds.includes(value)
      ) {
        setValue(`${fieldPrefix}${DealParticipantsField.DSP}`, value, { shouldDirty: true });
        setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, []);
        setValue(`${fieldPrefix}${DealParticipantsField.BUYER_ID}`, null);
        setValue(`${fieldPrefix}${DealParticipantsField.MAIN_BUYER}`, null);

        if (isAmazonDemandPartner && value !== AMAZON_DEMAND_PARTNER_ID) {
          // Clear the marketplace flag when this Amazon participant changes off Amazon.
          setValue(`${fieldPrefix}${DealParticipantsField.AMAZON_MARKETPLACE_ENABLED}`, false, { shouldDirty: true });
        }

        if (value !== TTD_DEMAND_PARTNER_ID && getValues(dealFormField.PDP_CONFIG) !== null) {
          setValue(dealFormField.PDP_CONFIG, null, { shouldDirty: true });
        }

        if (value === TTD_DEMAND_PARTNER_ID && observableDealType === pmpDealType.Guaranteed) {
          const currentPeriod = getValues(`${dealFormField.PDP_CONFIG}.period`);

          if (currentPeriod === null || currentPeriod === undefined) {
            setValue(`${dealFormField.PDP_CONFIG}.period`, PDP_SYNC_PERIOD_DEFAULT, { shouldDirty: false });
          }
        }

        if (value !== AMAZON_DEMAND_PARTNER_ID && getValues(dealFormField.AMAZON_CONFIG) !== null) {
          setValue(dealFormField.AMAZON_CONFIG, null, { shouldDirty: true });
        }

        trigger(dealFormField.NAME);
        trigger(dealFormField.START_DATE);
        trigger(dealFormField.END_DATE);
      } else {
        enqueueSnackbar(t('You do not have permission to select this DSP.'), { variant: 'error' });
      }
    },
    [
      enqueueSnackbar,
      t,
      blockedToSelectPartnerIds,
      fieldPrefix,
      getValues,
      setValue,
      trigger,
      observableDealType,
      isAmazonDemandPartner,
    ]
  );

  const sortedDemandPartnerOptions = useMemo(() => {
    const sortedDemandPartners = sortDSPOptions(demandPartnerOptions).filter(option => {
      return (
        !selectedDemandPartners.includes(option) ||
        // do not filter out selected option
        option === observableDemandPartner
      );
    });

    if (!isEdit && blockedToSelectPartnerIds && blockedToSelectPartnerIds.length > 0) {
      return sortedDemandPartners.filter(option => !blockedToSelectPartnerIds.includes(option));
    }

    return sortedDemandPartners;
  }, [demandPartnerOptions, blockedToSelectPartnerIds, isEdit, selectedDemandPartners, observableDemandPartner]);

  const demandPartnerType = participantFunctions.getDemandPartnerType(observableDemandPartner, dealType);
  const demandPartnerBuyerInfo = participantFunctions.getTranslationKeyForBuyerSeatInfo(
    demandPartnerType,
    t,
    isTTDPartnerAndGuaranteedDealType
  );

  const shouldDisplayDV360EndDateMessage =
    isDV360DemandPartner && observableDealSource !== DEAL_SOURCE_DSP_SOURCED && !observableEndDate;

  const handleRemove = () => {
    remove(index);
    trigger(dealFormField.NAME);
    trigger(dealFormField.START_DATE);
    trigger(dealFormField.END_DATE);
  };

  const dspsToShowOnTop = useMemo(() => {
    const options: [string, number | undefined][] = Object.values(demandPartnerOptions)
      .filter(option => option.show_on_top !== undefined)
      .map(option => [option.id, option.show_on_top]);

    return new Map(options);
  }, [demandPartnerOptions]);

  useEffect(() => {
    if (
      observableDemandPartner &&
      !demandPartnerOptionsLoading &&
      Object.keys(demandPartnerOptions).length &&
      !demandPartnerOptions[observableDemandPartner]
    ) {
      setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, []);
      setValue(`${fieldPrefix}${DealParticipantsField.MAIN_BUYER}`, null);
    }
  }, [fieldPrefix, observableDemandPartner, setValue, demandPartnerOptions, demandPartnerOptionsLoading]);

  useEffect(() => {
    if (observableAmazonMarketplace) {
      trigger(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`);
    }
  }, [observableAmazonMarketplace, fieldPrefix, trigger]);

  return (
    <>
      <Grid
        container
        wrap="nowrap"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid size={12} sx={{ minWidth: '100%' }}>
          <RHFAutoCompleteVirtualize
            name={`${fieldPrefix}${DealParticipantsField.DSP}`}
            textFieldProps={{
              label: t('DSPs'),
              margin: 'dense',
              placeholder: t('[ Select DSP ]'),
            }}
            disableClearable
            renderOptions={{
              dataTest: option => `deal-dsp-item-${index}-${option}`,
              tooltip: true,
            }}
            groupBy={option => (dspsToShowOnTop.has(option) ? `show-on-top` : 'dont-show-on-top')}
            itemSize={150}
            renderGroup={params => (
              <Fragment key={`group-${params.key}`}>
                {params.children}
                {params.key === 0 && (
                  <Box
                    sx={theme => ({
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      overflow: 'hidden',
                      margin: theme.spacing(0.5, 0),
                    })}
                  />
                )}
              </Fragment>
            )}
            getOptionLabel={option => demandPartnerOptions[option]?.name || t('N / A')}
            onChange={handlePartnerChange}
            value={demandPartnerOptionsLoading ? '' : observableDemandPartner} // wait for options loaded asynchronously
            options={sortedDemandPartnerOptions}
            loading={demandPartnerOptionsLoading}
            data-test="deal-dsp"
            disabled={isDemandPartnerDropdownDisabled(observableDemandPartner, isEdit, isDV360OnEditEntry.current)}
          />

          <IndentedSections sx={showParticipantMediaSpendGoal ? { mb: 3 } : undefined}>
            {showTTDCommitmentSectionAlert && (
              <Alert severity="info" sx={{ mb: 1 }} data-test="pdp-commitment-info-alert">
                <Trans
                  i18nKey="Selecting The Trade Desk as the DSP enables the additional &quot;<bold>TTD Financial and Cancellation Terms</bold>&quot; section below."
                  components={{ bold: <Box component="span" sx={{ fontWeight: 'fontWeightBold' }} /> }}
                />
              </Alert>
            )}

            {demandPartnerBuyerInfo && (
              <Alert data-test="dsp-buyer-info" severity="info">
                {demandPartnerBuyerInfo}
              </Alert>
            )}

            {shouldDisplayDV360EndDateMessage && (
              <Alert sx={{ marginTop: 2 }} data-test="dsp-end-date-info" severity="info">
                {t('Deal end date required by DSP')}
              </Alert>
            )}

            {showAmazonMarketplaceCheckbox && (
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <RHFCheckboxField
                  name={`${fieldPrefix}${DealParticipantsField.AMAZON_MARKETPLACE_ENABLED}`}
                  data-test="amazon-marketplace-enabled"
                  disabled={isMarketplaceEnabledOnLoad || isSyncEnabledOnLoad || !canCreateAmazonMarketplaceDeal}
                  onChange={(e, value) => {
                    if (value) {
                      e.preventDefault();
                      showAmazonMarketplaceConfirmation({
                        onConfirmed: () => {
                          setValue(`${fieldPrefix}${DealParticipantsField.AMAZON_MARKETPLACE_ENABLED}`, true, {
                            shouldDirty: true,
                          });
                          enqueueSnackbar(t('Amazon Marketplace option was applied successfully.'), {
                            variant: 'success',
                          });
                        },
                        onCancel: () => {
                          // user cancelled — keep field unchecked
                        },
                      });
                    }
                  }}
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t('Enable Marketplace')}
                      <Tooltip
                        placement="right"
                        title={t(
                          'When enabled, Amazon Marketplace logic takes precedence, and all Seat IDs for this participant are ignored.'
                        )}
                      >
                        <IconButton aria-label={t('Amazon Marketplace info')} size="small" sx={{ p: 0 }}>
                          <InfoRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>
            )}

            {showAmazonMarketplaceCheckbox && observableAmazonMarketplace && (
              <>
                <Alert sx={{ mt: 1 }} data-test="amazon-marketplace-active-info" severity="info">
                  {t('Seat IDs are ignored while Marketplace is active.')}
                </Alert>
                <RHFAutoCompleteVirtualize
                  name={`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`}
                  options={[]}
                  textFieldProps={{
                    label: t('DSP Seat IDs'),
                    margin: 'dense',
                    placeholder: t('[ Select DSP Seat IDs ]'),
                  }}
                  disabled
                  data-test="deal-dsp-seat-ids-marketplace-disabled"
                />
              </>
            )}

            {isToggleEnableDV360IPVisible(observableDemandPartner, isAllowed) && (
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <RHFSwitchField
                  checked={!!observableIsDV360IP}
                  name={dealFormField.ENABLE_DV360_INVENTORY_PACKAGE}
                  label={t('Include in DV360 Inventory Package')}
                  type="checkbox"
                  data-test="is-enable-dv360-inventory-package"
                  disabled={isEdit}
                />
              </Box>
            )}

            {showParticipantBuyers && (
              <ParticipantBuyers
                buyers={buyers}
                demandPartnerType={demandPartnerType}
                fieldPrefix={fieldPrefix}
                index={index}
                isBuyersLoading={isBuyersLoading}
                isEdit={isEdit}
                isTTDPartnerAndGuaranteedDealType={isTTDPartnerAndGuaranteedDealType}
              />
            )}

            {showParticipantBuyersFreeForm && !isAmazonMarketplaceActive && (
              <ParticipantBuyersFreeForm fieldPrefix={fieldPrefix} />
            )}

            {showParticipantMediaSpendGoal && (
              <>
                <Typography variant="overline" sx={{ textTransform: 'uppercase', mt: 3, mb: 0.5, display: 'block' }}>
                  {t('TTD Financial Terms')}
                </Typography>
                <FormFieldWithInfo
                  info={
                    <Typography variant="caption">
                      {t('Currency is inherited from the Deal Floor settings.')}
                    </Typography>
                  }
                  sx={{ mt: 0.5 }}
                >
                  <RHFTextFieldWithAdornment
                    dataTest="pdp-config-media-spend-goal"
                    formControlProps={{ margin: 'dense' }}
                    label={t('Media Spend Goal (Optional)')}
                    name={`${dealFormField.PDP_CONFIG}.media_spend_goal`}
                    placeholder={t('Enter Value')}
                    endAdornment={observableCurrency && currencySymbol(observableCurrency)}
                  />
                </FormFieldWithInfo>
              </>
            )}

            {showParticipantBudget && (
              <FormFieldWithInfo
                info={<Typography variant="caption">{t('Determines the agreed guaranteed budget')}</Typography>}
                sx={{ mt: 0.5 }}
              >
                <RHFTextFieldWithAdornment
                  dataTest="amazon-config-budget"
                  formControlProps={{ margin: 'dense' }}
                  label={t('Budget')}
                  name={`${dealFormField.AMAZON_CONFIG}.budget`}
                  placeholder={t('Enter Value')}
                  endAdornment={observableCurrency && currencySymbol(observableCurrency)}
                />
              </FormFieldWithInfo>
            )}
          </IndentedSections>

          {!isOa && <ParticipantBrands index={index} />}
        </Grid>

        {showRemove && (
          <Box sx={{ paddingTop: 3.25 }}>
            <IconButton onClick={handleRemove} data-test={`delete-icon-${index}`} size="large">
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Grid>

      {showDivider && <Divider sx={{ marginBottom: 2, marginTop: 3 }} />}
    </>
  );
};
